import React from "react";
import { Button } from "../../../../components/ui/button";
import { RefreshCw, StopCircle } from "lucide-react";
import { useEffectiveRole, canAtLeast } from "../../../../lib/roles";

/**
 * Cancel / retry action strip. Role-gated:
 *   - Manager+ → shows cancel (during COMMITTING) or retry (PARTIAL / FAILED).
 *   - Representative (or unknown role) → shows a read-only notice.
 *   - Viewers are blocked upstream by role/route guards.
 */
export function ActionRow({
  job,
  onCancelClick,
  onRetryClick,
}: {
  job: { status: string };
  onCancelClick: () => void;
  onRetryClick: () => void;
}) {
  const role = useEffectiveRole();
  const canMutate = canAtLeast(role, "manager_trainer");
  const canCancel = job.status === "COMMITTING";
  const canRetry = job.status === "PARTIAL" || job.status === "FAILED";

  if (!canMutate) {
    if (!canCancel && !canRetry) return null;
    return (
      <div
        className="text-xs text-slate-500 pt-2 border-t border-slate-100"
        data-testid="step-5-actions-readonly"
      >
        View only — a manager can {canCancel ? "cancel" : "retry"} this import.
      </div>
    );
  }
  if (!canCancel && !canRetry) return null;
  return (
    <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
      {canCancel && (
        <Button
          variant="outline"
          className="rounded-full"
          onClick={onCancelClick}
          data-testid="cancel-btn"
        >
          <StopCircle className="w-4 h-4 mr-1" /> Cancel import
        </Button>
      )}
      {canRetry && (
        <Button
          className="rounded-full"
          onClick={onRetryClick}
          data-testid="retry-btn"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Retry failed rows
        </Button>
      )}
    </div>
  );
}
